/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { css } from "@emotion/css";
import { EmptyStateNoResults, Pagination } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { ChangeRequestApprovalState, PaginatedCollection, Repository, ServerTaskApprovalBffResponse } from "@octopusdeploy/octopus-server-client";
import type { QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import * as React from "react";
import type { ApprovalsPageLoaderData, ApprovalsPageQueryParams } from "~/areas/approvals/components/ServerTaskApprovals/ServerTaskApprovalsPage";
import { BorderedContent } from "~/components/BorderedContent/BorderedContent";
import type { Item } from "~/primitiveComponents/form/Select/Select";
import Select from "~/primitiveComponents/form/Select/Select";
import { ControlledTabsContainer, TabItem } from "~/primitiveComponents/navigation/Tabs/index";
import { ServerTaskApprovalsTable } from "./ServerTaskApprovalsTable";
const defaultPageSize = 30;
interface ApprovalsProps {
    queryParams: ApprovalsPageQueryParams;
    setQueryParams: QueryParamValuesSetter<ApprovalsPageQueryParams>;
    loaderData: ApprovalsPageLoaderData;
}
export function ServerTaskApprovals({ queryParams, setQueryParams, loaderData }: ApprovalsProps) {
    const approvalsQuery = usePagedApprovalsQuery(queryParams, loaderData.approvalsListData);
    const onPageChange = React.useCallback((page: number) => setQueryParams({ ...queryParams, page }), [queryParams, setQueryParams]);
    const onPageSizeChange = React.useCallback((pageSize: number) => setQueryParams({ ...queryParams, pageSize, page: 1 }), [queryParams, setQueryParams]);
    const handleStatusChange = React.useCallback((status: "all" | "pending" | "approved") => {
        setQueryParams({ ...queryParams, status, page: 1 });
    }, [queryParams, setQueryParams]);
    const handleProviderChange = React.useCallback((providerId: string) => {
        setQueryParams({ ...queryParams, providerId, page: 1 });
    }, [queryParams, setQueryParams]);
    const handleSpacesChange = React.useCallback((spaces: string[]) => {
        setQueryParams({ ...queryParams, spaces, page: 1 });
    }, [queryParams, setQueryParams]);
    const { approvals } = approvalsQuery.result;
    const hideBottomPagination = approvals.Items.length < 20;
    const pendingLabel = loaderData.approvalStats.pendingCount === null ? "Pending" : `Pending (${loaderData.approvalStats.pendingCount})`;
    const completedLabel = loaderData.approvalStats.completedCount === null ? "Completed" : `Completed (${loaderData.approvalStats.completedCount})`;
    const tableContent = approvals.Items.length === 0 ? <EmptyStateNoResults /> : <ServerTaskApprovalsTable serverTaskApprovals={approvals.Items}/>;
    const filterContent = (<div className={filterContainerStyles}>
            <div className={filterSectionStyles}>
                <FilterSection queryParams={queryParams} setQueryParams={setQueryParams} onProviderChange={handleProviderChange}/>
            </div>
            <BorderedContent>{tableContent}</BorderedContent>
        </div>);
    return (<div>
            <div className={paginationContainerStyles}>
                <Pagination label="Approvals" totalResults={approvals.TotalResults} itemsPerPageOptions={[defaultPageSize, 50, 100]} selectedItemsPerPage={queryParams.pageSize || defaultPageSize} currentPage={queryParams.page ?? 1} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange}/>
            </div>
            <ControlledTabsContainer onChange={(value) => handleStatusChange(value as "all" | "pending" | "approved")} value={queryParams.status || "all"}>
                <TabItem label="All" value="all">
                    {filterContent}
                </TabItem>
                <TabItem label={pendingLabel} value="pending">
                    {filterContent}
                </TabItem>
                <TabItem label={completedLabel} value="approved">
                    {filterContent}
                </TabItem>
            </ControlledTabsContainer>
            {!hideBottomPagination && (<div className={paginationContainerStyles}>
                    <Pagination label="Approvals" totalResults={approvals.TotalResults} itemsPerPageOptions={[defaultPageSize, 50, 100]} selectedItemsPerPage={queryParams.pageSize || defaultPageSize} currentPage={queryParams.page ?? 1} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange}/>
                </div>)}
        </div>);
}
async function getPaginatedApprovals(repository: Repository, queryParams: ApprovalsPageQueryParams): Promise<{
    approvals: PaginatedCollection<ServerTaskApprovalBffResponse>;
}> {
    const stateFilter: undefined | ChangeRequestApprovalState = queryParams.status === "all" ? undefined : queryParams.status === "pending" ? "preapproval" : "approved";
    const skip = ((queryParams.page || 1) - 1) * (queryParams.pageSize || defaultPageSize);
    const requestParams = {
        skip,
        take: queryParams.pageSize || defaultPageSize,
        providerId: queryParams.providerId,
        state: stateFilter,
        spaces: queryParams.spaces || [],
    };
    const approvalsResponse = await repository.ServerTaskApprovals.listBff(requestParams);
    return {
        approvals: approvalsResponse.ServerTaskApprovals,
    };
}
function usePagedApprovalsQuery(queryParams: ApprovalsPageQueryParams, initialResult: {
    approvals: PaginatedCollection<ServerTaskApprovalBffResponse>;
}) {
    return useQuery((repository) => getPaginatedApprovals(repository, queryParams), [queryParams], "Get paginated approvals", {
        initialResult,
    });
}
interface FilterSectionProps {
    queryParams: ApprovalsPageQueryParams;
    setQueryParams: QueryParamValuesSetter<ApprovalsPageQueryParams>;
    onProviderChange: (providerId: string) => void;
}
function FilterSection({ queryParams, setQueryParams, onProviderChange }: FilterSectionProps) {
    const providerItems: Item[] = [
        { value: "octopus", text: "Octopus" },
        { value: "servicenow", text: "ServiceNow" },
        { value: "jsm", text: "Jira Service Management" },
    ];
    return (<>
            <div className={providersStyles}>
                <Select value={queryParams.providerId} onChange={(value) => onProviderChange(value ?? "")} items={providerItems} allowClear={true} fieldName="provider" placeholder="All providers"/>
            </div>
        </>);
}
const filterContainerStyles = css({
    margin: `${space[16]} 0`,
});
const filterSectionStyles = css({
    display: "flex",
    gap: space[24],
    marginBottom: space[16],
});
const providersStyles = css({
    width: "16rem",
});
const loadingStyles = css({
    display: "flex",
    justifyContent: "center",
    paddingBottom: space[16],
});
const paginationContainerStyles = css({
    margin: `${space[16]} 0`,
});
